import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Trash2, Plus, Receipt } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import ProjectStatusChip from '../../components/crm/ProjectStatusChip';
import DeliverableChecklist from '../../components/crm/DeliverableChecklist';
import DeployHistory from '../../components/crm/DeployHistory';
import RunAuditButton from '../../components/admin/RunAuditButton';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import ProjectStageTransitionDialog from '../../components/admin/ProjectStageTransitionDialog';
import NewInvoiceDialog from '../../components/admin/NewInvoiceDialog';
import BackLink from '../../components/admin/BackLink';
import InvoiceStatusChip from '../../components/crm/InvoiceStatusChip';
import EmptyState from '../../components/crm/EmptyState';
import { fmtCents } from '../../lib/financial';
import { PROJECT_TRANSITIONS, projectStatusMeta } from '../../lib/projects';

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [account, setAccount] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusOpen, setStatusOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [forceStatusOpen, setForceStatusOpen] = useState(false);
  // F11 — normal "Move to" also goes through the stage form so the facts it
  // collects (preview URL, go-live date, consent…) are captured and written.
  const [transitionTarget, setTransitionTarget] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/admin/projects/${id}`);
      setProject(res.project);
      setDeliverables(res.deliverables || []);
      setHistory(res.history || []);
      if (res.project?.accountId) {
        try {
          const acc = await api.get(`/api/admin/accounts/${res.project.accountId}`);
          setAccount(acc.account);
        } catch { /* non-fatal — invoice section will gate on account presence */ }
      }
    } catch (err) {
      setError(err.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = (target) => {
    setStatusOpen(false);
    setTransitionTarget(target);
  };

  const doDelete = async () => {
    await api.delete(`/api/admin/projects/${id}`);
    navigate('/admin/projects');
  };

  const isAdmin = user?.role === 'nexsav_admin';
  const isStaff = user?.role === 'nexsav_admin' || user?.role === 'ops';
  const allowed = project ? (PROJECT_TRANSITIONS[project.status] || []) : [];

  return (
    <div className="max-w-5xl mx-auto">
      <BackLink to="/admin/projects" className="inline-flex items-center gap-1.5 text-[12px] text-gray-500 hover:text-white mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to projects
      </BackLink>

      {loading && !project && <div className="animate-pulse h-20 rounded-xl bg-white/[0.03]" />}
      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>}

      {project && (
        <>
          <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight">{project.name}</h1>
              <div className="text-[12px] text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                <span className="font-mono">{project.slug}</span>
                <span className="uppercase tracking-wider text-gray-600">· {project.type}</span>
              </div>
            </div>
            <RunAuditButton account={{ id: project.accountId, siteUrl: project.liveUrl || project.previewUrl }} />
          </div>

          <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[13px] font-semibold text-white">Status</h3>
              <div className="relative">
                <button onClick={() => setStatusOpen((v) => !v)} className="inline-flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-white/[0.03]">
                  <ProjectStatusChip status={project.status} />
                  <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                </button>
                {statusOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setStatusOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-50 min-w-[220px] rounded-xl border border-white/[0.08] bg-[#0a0a0f] p-1.5 shadow-lg">
                      <div className="text-[10px] text-gray-600 uppercase tracking-wider px-2 py-1">Move to</div>
                      {allowed.length === 0 && <div className="px-2 py-2 text-[12px] text-gray-500">No valid transitions</div>}
                      {allowed.map((s) => (
                        <button key={s} onClick={() => changeStatus(s)} className="w-full text-left px-2 py-1.5 rounded-lg text-[12px] text-gray-300 hover:bg-white/[0.04] hover:text-white">
                          {projectStatusMeta(s).label}
                        </button>
                      ))}
                      {isStaff && (
                        <>
                          <div className="border-t border-white/[0.05] my-1" />
                          <button onClick={() => { setStatusOpen(false); setForceStatusOpen(true); }}
                            data-testid="project-force-status-open"
                            className="w-full text-left px-2 py-1.5 rounded-lg text-[12px] text-amber-300 hover:bg-amber-500/10">
                            Force any status…
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-[12px]">
              <KV label="Entered"><span className="text-gray-300">{String(project.statusEnteredAt || '').slice(0, 16)}</span></KV>
              {project.targetDeliveryDate && <KV label="Target delivery"><span className="text-gray-300">{project.targetDeliveryDate}</span></KV>}
              {project.actualDeliveryDate && <KV label="Delivered"><span className="text-gray-300">{String(project.actualDeliveryDate).slice(0, 16)}</span></KV>}
              {project.previewUrl && <KV label="Preview"><a href={project.previewUrl} target="_blank" rel="noopener noreferrer" className="text-[#B0FC38] hover:text-white">{project.previewUrl}</a></KV>}
              {project.liveUrl && <KV label="Live URL"><a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="text-[#B0FC38] hover:text-white">{project.liveUrl}</a></KV>}
              {isAdmin && 'contractValue' in project && <KV label="Contract value"><span className="text-gray-300">{project.contractValue != null ? `$${(project.contractValue / 100).toFixed(2)}` : '—'}</span></KV>}
            </div>
          </div>

          {project.type !== 'maintenance' && (
            <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mb-6">
              <h3 className="text-[13px] font-semibold text-white mb-3">Deliverables ({deliverables.length})</h3>
              <DeliverableChecklist projectId={project.id} deliverables={deliverables} onChange={setDeliverables} />
            </div>
          )}

          <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mb-6">
            <h3 className="text-[13px] font-semibold text-white mb-3">Deploy history</h3>
            <DeployHistory projectId={project.id} />
          </div>

          <ProjectStatusHistory history={history} />

          {isStaff && (
            <ProjectInvoicesSection
              projectId={project.id}
              account={account}
              onSeeAll={() => navigate(`/admin/accounts/${project.accountId}?tab=financial`)}
            />
          )}

          {isAdmin && (
            <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5">
              <h3 className="text-[13px] font-semibold text-white mb-2">Danger zone</h3>
              <button onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20">
                <Trash2 className="w-3 h-3" /> Delete project
              </button>
              <div className="text-[11px] text-gray-500 mt-2">Cascades to deliverables.</div>
            </div>
          )}

          <ConfirmDialog
            open={confirmDelete}
            onClose={() => setConfirmDelete(false)}
            onConfirm={doDelete}
            title="Delete project?"
            description={`Permanently removes "${project.name}" and all deliverables.`}
            confirmLabel="Delete"
            destructive
          />
          <ProjectStageTransitionDialog
            open={forceStatusOpen}
            project={project}
            forceMode
            onClose={() => setForceStatusOpen(false)}
            onConfirm={async (payload) => {
              await api.post(`/api/admin/projects/${id}/status`, payload);
              await load();
            }}
          />
          <ProjectStageTransitionDialog
            open={!!transitionTarget}
            project={project}
            defaultTarget={transitionTarget}
            onClose={() => setTransitionTarget('')}
            onConfirm={async (payload) => {
              await api.post(`/api/admin/projects/${id}/status`, payload);
              setTransitionTarget('');
              await load();
            }}
          />
        </>
      )}
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div>
      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function ProjectStatusHistory({ history }) {
  if (!history || history.length === 0) {
    return (
      <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mb-6">
        <h3 className="text-[13px] font-semibold text-white mb-3">Status history</h3>
        <div className="text-[12px] text-gray-500">No status transitions recorded yet.</div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mb-6">
      <h3 className="text-[13px] font-semibold text-white mb-3">Status history ({history.length})</h3>
      <div className="space-y-2">
        {history.map((h) => (
          <div key={h.id} className="p-3 rounded-xl border border-white/[0.06] bg-white/[0.02]">
            <div className="flex items-center gap-2 flex-wrap">
              {h.statusFrom && <ProjectStatusChip status={h.statusFrom} />}
              <span className="text-gray-500 text-[11px]">→</span>
              <ProjectStatusChip status={h.statusTo} />
              {h.forced && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300">
                  Forced
                </span>
              )}
              <span className="ml-auto text-[11px] text-gray-500">
                {String(h.transitionedAt || '').replace('T', ' ').slice(0, 16)}
              </span>
            </div>
            <div className="text-[11px] text-gray-500 mt-1.5">
              by {h.actor?.name || h.actor?.email || 'Unknown'}
            </div>
            {h.overrideReason && (
              <div className="mt-2 px-3 py-2 rounded-lg bg-amber-500/[0.06] border border-amber-500/20 text-[12px] text-amber-200/90">
                <div className="text-[10px] uppercase tracking-wider text-amber-300/70 mb-0.5">Override reason</div>
                {h.overrideReason}
              </div>
            )}
            {h.summary && !h.overrideReason && (
              <div className="mt-2 text-[12px] text-gray-300">{h.summary}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectInvoicesSection({ projectId, account, onSeeAll }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/admin/financial/invoices?project_id=${encodeURIComponent(projectId)}`);
      setInvoices(res?.invoices || []);
    } catch (err) {
      setError(err?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mb-6">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-[13px] font-semibold text-white">Invoices ({invoices.length})</h3>
        <div className="flex items-center gap-2">
          {account && (
            <button
              onClick={onSeeAll}
              className="text-[11px] text-gray-400 hover:text-white"
            >
              See all on account →
            </button>
          )}
          <button
            onClick={() => setNewOpen(true)}
            disabled={!account}
            data-testid="project-new-invoice"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            title={!account ? 'Loading account…' : 'Create invoice for this project'}
          >
            <Plus className="w-3 h-3" /> New invoice
          </button>
        </div>
      </div>

      {error && (
        <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">
          {error}
        </div>
      )}

      {loading && invoices.length === 0 ? (
        <div className="animate-pulse h-12 rounded-lg bg-white/[0.03]" />
      ) : invoices.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices for this project yet" />
      ) : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <button
              key={inv.id}
              onClick={() => navigate(`/admin/invoices/${inv.id}`)}
              className="w-full text-left p-3 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-[13px] text-white font-mono">{inv.invoiceNumber}</div>
                <div className="text-[11px] text-gray-500">
                  Issued {inv.issuedAt}{inv.dueAt ? ` · Due ${inv.dueAt}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <InvoiceStatusChip status={inv.status} />
                {inv.isOverdue && <span className="text-[10px] text-red-300 uppercase tracking-wider">overdue</span>}
                <div className="text-right">
                  <div className="text-[13px] text-white">{fmtCents(inv.amountCents, inv.currency)}</div>
                  {typeof inv.outstandingCents === 'number' && (
                    <div className="text-[10px] text-gray-500">{fmtCents(inv.outstandingCents)} open</div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {account && (
        <NewInvoiceDialog
          open={newOpen}
          onClose={() => setNewOpen(false)}
          defaultAccountLocked
          prefill={{ account, projectId }}
          onCreated={async () => { await load(); setNewOpen(false); }}
        />
      )}
    </div>
  );
}
